import { BillingHistoryItem } from '../../redux/slices/subscriptionSlice';
import styled from '@emotion/styled';
import { format } from 'date-fns';

interface BillingHistoryTableProps {
  history: BillingHistoryItem[];
}

const BillingHistoryTable = ({ history }: BillingHistoryTableProps) => {
  if (history.length === 0) {
    return (
      <EmptyState>
        <Icon>📄</Icon>
        <Title>No Billing History</Title>
        <Description>Your payment history will appear here</Description>
      </EmptyState>
    );
  }

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Plan</th>
            <th>Amount</th>
            <th>Interval</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {history.map((item) => (
            <tr key={item.id}>
              <td>{format(new Date(item.paidAt || item.createdAt), 'MMM d, yyyy')}</td>
              <td>{item.planName}</td>
              <td>₦{Number(item.amount).toLocaleString('en-NG', { maximumFractionDigits: 0 })}</td>
              <td>{item.billingInterval}</td>
              <td>
                <StatusBadge status={item.status}>{item.status}</StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
};

export default BillingHistoryTable;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.palette.greyGrey3};
`;

const Icon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const Title = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.875rem;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  }

  th {
    background: ${({ theme }) => theme.palette.greyGrey4};
    font-weight: 600;
    color: ${({ theme }) => theme.palette.greyGrey1};
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: ${({ theme }) => theme.palette.greyGrey4};
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${({ theme, status }) => {
    switch (status) {
      case 'SUCCESS':
        return 'rgba(16, 185, 129, 0.1)';
      case 'FAILED':
        return 'rgba(239, 68, 68, 0.1)';
      case 'PENDING':
        return 'rgba(245, 158, 11, 0.1)';
      case 'REFUNDED':
        return 'rgba(107, 114, 128, 0.1)';
      default:
        return theme.palette.greyGrey5;
    }
  }};
  color: ${({ theme, status }) => {
    switch (status) {
      case 'SUCCESS':
        return theme.palette.stateColorGreen;
      case 'FAILED':
        return theme.palette.stateColorRed;
      case 'PENDING':
        return theme.palette.stateColorYellow;
      case 'REFUNDED':
        return theme.palette.greyGrey3;
      default:
        return theme.palette.greyGrey2;
    }
  }};
`;
